import React from 'react';
import { Route } from 'react-router-dom';
import PropTypes from 'prop-types';
import { NavigationDrawer, ListItem, Divider } from 'react-md';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { compact, filter, get } from 'lodash-es';
import ReactMarkdown from 'react-markdown';

import { SnackbarProvider } from 'notistack';

import KebabMenu from './KebabMenu';
import NavLink from '../NavLink/NavLink';
import ExtraMenuBlock from './ExtraMenuBlock';
import ModalBox from '../ModalBox/ModalBox';
import Logo from '../Logo/Logo';
import * as authActions from '../../Services/Auth/actions';
import * as systemActions from '../../Services/System/actions';
import * as themeActions from '../../Services/Theme/actions';

import './Layout.scss';

const Layout = ({
  component: Component,
  defaultNavItems,
  navItems: dynaNavItems,
  extraMenu,
  strings,
  isAuthenticated,
  userInfo,
  menuDrawerSettings,
  showAbout,
  serviceInfo,
  ready,
  info,
  logo,
  currentTheme,
  ...rest
}) => {
  const {
    // path,
    actions,
  } = rest;
  const { visible } = menuDrawerSettings;
  defaultNavItems.forEach(nav => {
    const label = get(nav, 'label');
    nav.label = get(strings, label, label); // eslint-disable-line
  }); // eslint-disable-line no-param-reassign
  const internalNavItems = [...defaultNavItems, ...dynaNavItems];
  // const { username } = userInfo ? userInfo.session : { username: 'Anonymous' };
  // const { name } = userInfo && userInfo.pref ? userInfo.pref : { username: 'Anonymous' };
  const name = get(userInfo, 'name') || get(userInfo, 'pref.name', 'Anonymous');

  const initialUserInfo = () => {
    if (!ready) actions.fetchUserInfoRequest();
  };

  const handleLogout = event => {
    actions.logout(event);
  };

  const toggleAbout = () => {
    actions.fetchServiceInfoRequest();
    actions.toggleAbout(showAbout);
  };

  const menuItemLoggedIsAs = (
    <span className="menuItem-loggedIsAs">
      <span>{get(strings, 'loggedIsAs')}</span>
      <b>{name}</b>
    </span>
  );
  const menuItems = [
    // <ListItem key={0} component={Link} to="/settings" primaryText={menuItemLoggedIsAs} />,
    <ListItem key={0} primaryText={menuItemLoggedIsAs} />,
    // <Divider key={1} />,
    // <ListItem key={2} component={Link} to="/settings" primaryText={strings.settings} />,
    <Divider key={3} />,
    <ListItem key={4} primaryText={get(strings, 'about', '')} onClick={toggleAbout} />,
    <Divider key={5} />,
    <ListItem
      key={6}
      primaryText={isAuthenticated ? get(strings, 'logoutLabel', '') : get(strings, 'loginLabel', 'logout')}
      onClick={handleLogout}
    />,
  ];
  const toolbarActions = (
    <KebabMenu id="toolbar-transparent-kebab-menu" iconName="account_circle" menuItems={menuItems} />
  );
  const handleVisibilityChange = state => {
    actions.updateMenuDrawerSettings({
      visible: state,
    });
  };

  const extras = () => <ExtraMenuBlock key="n-extra" extraMenu={extraMenu} />;

  const navItems = () => {
    const internals = compact(
      filter(internalNavItems, navItem => !navItem.onlyAdmin || (navItem.onlyAdmin && userInfo.isFrontAdmin))
    ).map(props => <NavLink {...props} key={`n-${get(props, 'to')}`} />);
    return [...internals, extras()];
  };

  const drawerTitle = (
    <div className="drawerTitle">
      <Logo logo={logo} strings={strings} />
    </div>
  );

  const title = source => <ReactMarkdown className="markdown">{source}</ReactMarkdown>;

  const toolbarTitle = (
    <div className="toolbarTitle">
      <span className="label">{title(get(strings, 'toolbarTitle'))}</span>
      <span className="user">{name}</span>
    </div>
  );

  return (
    <>
      <SnackbarProvider maxSnack={5}>
        <NavigationDrawer
          id="navigation-drawer"
          drawerId="drawer-drawer"
          miniDrawerId="mini-drawer-drawer"
          navItemsId="nav-items-drawer"
          miniNavItemsId="mini-nav-items-drawer"
          drawerTitle={drawerTitle}
          toolbarTitle={toolbarTitle}
          toolbarClassName={`toolbar-coog ${currentTheme}`}
          mobileDrawerType={NavigationDrawer.DrawerTypes.TEMPORARY_MINI}
          tabletDrawerType={NavigationDrawer.DrawerTypes.PERSISTENT_MINI}
          desktopDrawerType={NavigationDrawer.DrawerTypes.PERSISTENT_MINI}
          // desktopDrawerType={NavigationDrawer.DrawerTypes.CLIPPED}
          onVisibilityChange={handleVisibilityChange}
          visible={visible}
          navItems={navItems()}
          toolbarActions={toolbarActions}
        >
          <Route
            {...rest}
            render={matchProps => {
              initialUserInfo();
              return <Component {...matchProps} />;
            }}
          />
        </NavigationDrawer>
      </SnackbarProvider>
      <ModalBox show={showAbout} toggle={toggleAbout} data={serviceInfo} logo={logo} strings={strings} />
    </>
  );
};

Layout.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  userInfo: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  ready: PropTypes.bool,
  component: PropTypes.func.isRequired,
  exact: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    logout: PropTypes.func.isRequired,
    settings: PropTypes.func,
    fetchUserInfoRequest: PropTypes.func.isRequired,
  }),
  path: PropTypes.string.isRequired,
  navItems: PropTypes.arrayOf(PropTypes.any),
  defaultNavItems: PropTypes.arrayOf(PropTypes.any),
  extraMenu: PropTypes.arrayOf(PropTypes.any),
  strings: PropTypes.shape({
    toolbarTitle: PropTypes.string,
    drawerTitle: PropTypes.string,
    loginLabel: PropTypes.string,
    logoutLabel: PropTypes.string,
    settings: PropTypes.string,
    loggedIsAs: PropTypes.string,
    username: PropTypes.string,
    about: PropTypes.string,
  }),
  menuDrawerSettings: PropTypes.objectOf(PropTypes.any),
  showAbout: PropTypes.bool,
  serviceInfo: PropTypes.arrayOf(PropTypes.object),
  info: PropTypes.objectOf(PropTypes.object),
  logo: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  currentTheme: PropTypes.string,
};

Layout.defaultProps = {
  userInfo: {},
  ready: false,
  strings: {
    drawerTitle: 'Coog Portal',
    toolbarTitle: 'Sales Assistant',
    loginLabel: 'Login',
    logoutLabel: 'Logout',
    settings: 'Settings',
    loggedIsAs: 'logged in as ',
    username: 'Jean Claude Convenant',
  },
  navItems: [
    {
      exact: true,
      label: 'Party',
      to: `/party`,
      icon: 'supervised_user_circle',
    },
  ],
  defaultNavItems: [
    {
      exact: true,
      label: 'home',
      to: '/',
      icon: 'dashboard',
    },
  ],
  extraMenu: [],
  actions: {
    logout: (...event) => {
      // eslint-disable-next-line no-console
      console.log('Layout::logout', event);
    },
    settings: (...event) => {
      // eslint-disable-next-line no-console
      console.log('Layout::settings', event);
    },
  },
  menuDrawerSettings: {
    visible: false,
  },
  showAbout: false,
  serviceInfo: [],
  info: [],
  logo: {},
  currentTheme: 'coog',
};

const mapStateToProps = state => {
  const system = state.get('system').toJS();
  const auth = state.get('auth').toJS();
  const plugins = state.get('plugins').toJS();
  const theme = state.get('theme').toJS();

  return {
    logo: get(theme, 'logo', {}),
    currentTheme: get(theme, 'current', 'coog'),
    userInfo: get(auth, 'userInfo', {}),
    info: get(auth, 'info', {}),
    ready: get(auth, 'ready', false),
    navItems: get(system, 'navItems', []),
    strings: get(system, 'menuStrings', {}),
    menuDrawerSettings: get(system, 'menuDrawerSettings', {}),
    showAbout: get(system, 'showAbout', false),
    serviceInfo: get(system, 'serviceInfo', []),
    extraMenu: get(plugins, 'extraMenu', []),
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ ...authActions, ...systemActions, ...themeActions }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Layout);
